import { useActiveOrder, ActiveOrderProduct } from './active-order';
import { useZone } from './zone';
import { useCurrency } from './currency';
import { useDiscount } from './discount';
import { MenuProduct } from '../XV2';
import { ModifierGroupItem } from '../XV2';
import sum from 'lodash/sum';

export function useActiveOrderManager() {
    const activeOrder = useActiveOrder();
    const activeZone = useZone();
    const currency = useCurrency();
    const discount = useDiscount();
    const { serviceFeeConfig } = activeZone;
    let { isServiceFeeDisplayedInList, serviceFeeAmount, serviceFeePercent } = activeZone.location;

    if (serviceFeeConfig) {
        serviceFeeAmount = serviceFeeConfig.data.defaultFee ?? 0;
    }

    const extraFee = activeOrder.hasProductsWithPrices() ? 0 : serviceFeeAmount;
    const { hasTakeaway } = activeZone.response;

    function calculateProductDiscount(productId: number, price: number) {
        const productDiscount = discount.getDiscountByProductId(productId);
        return productDiscount ? currency.calculateDiscount(price, productDiscount.percent) : price;
    }

    function getRegularOrTakeawayPrice(menuProduct: Pick<MenuProduct, 'price' | 'priceTakeaway'>) {
        if (hasTakeaway) {
            return menuProduct.priceTakeaway ?? menuProduct.price;
        } else {
            return menuProduct.price || 0;
        }
    }

    function getPriceWithServiceFee(price: number) {
        if (!activeOrder.hasProductsWithPrices() && price === 0) {
            return price;
        }

        return price + extraFee;
    }

    function calculateActiveOrderProductPrice(activeOrderProduct: ActiveOrderProduct) {
        let price = activeOrderProduct.variantGroupProduct
            ? calculateProductDiscount(
                  activeOrderProduct.variantGroupProduct.productId,
                  activeOrderProduct.variantGroupProduct.price,
              )
            : calculateProductDiscount(
                  activeOrderProduct.productId,
                  getRegularOrTakeawayPrice(activeOrderProduct.menuProduct),
              );

        let priceModifiers = 0;

        const modifierGroupsIds = Object.keys(activeOrderProduct.modifierGroups);

        for (const groupId of modifierGroupsIds) {
            const modifierGroupItems = Object.values<ModifierGroupItem>(activeOrderProduct.modifierGroups[groupId]);

            for (const modifierGroupItem of modifierGroupItems) {
                priceModifiers += modifierGroupItem.price;
            }
        }

        price += priceModifiers;

        if (isServiceFeeDisplayedInList) {
            return getPriceWithServiceFee(price);
        } else {
            return price;
        }
    }

    function getServiceFee() {
        const totalPrice = getTotalPriceWithoutServiceFee();

        if (totalPrice > 0 && serviceFeeConfig) {
            const match = serviceFeeConfig.data.groups.find(
                (group) => group.from <= totalPrice && group.to >= totalPrice,
            );

            if (match) {
                return match.feeAmount;
            }
        }

        if (activeOrder.hasProductsWithPrices()) {
            let total = 0;

            if (serviceFeeAmount) {
                total += serviceFeeAmount;
            }

            if (serviceFeePercent) {
                total += currency.toNumber(getTotalPriceWithoutServiceFee() * (serviceFeePercent / 100));
            }

            return total;
        }

        return 0;
    }

    function hasActiveOrder() {
        return activeOrder.hasActiveOrder;
    }

    function getTotalPriceWithoutServiceFee() {
        return sum(activeOrder.orderProducts.map(calculateActiveOrderProductPrice));
    }

    function getTotalPrice() {
        return getTotalPriceWithoutServiceFee() + getServiceFee();
    }

    return {
        hasServiceFeeConfig: () => Boolean(serviceFeeConfig),
        isServiceFeeDisplayedInList: isServiceFeeDisplayedInList,
        hasActiveOrder: hasActiveOrder,
        getServiceFee: getServiceFee,
        getTotalPriceWithoutServiceFee: getTotalPriceWithoutServiceFee,
        getTotalPrice: getTotalPrice,
        calculateProductDiscount: calculateProductDiscount,
        getRegularOrTakeawayPrice: getRegularOrTakeawayPrice,
        getPriceWithServiceFee: getPriceWithServiceFee,
        calculateActiveOrderProductPrice: calculateActiveOrderProductPrice,
    };
}
